import { useCallback, useState, useEffect } from "react";
import { useLoginForm } from "../hooks/useLoginForm";
import { useMFAFlow } from "../hooks/useMFAFlow";
import { useLocation, useNavigate } from "react-router-dom";
import { useLogin, useAuth, useAuthStoreBase, getOktaSessionMe } from "@fmc-digital/core-auth";
import { cn } from "@fmc-digital/ui";
import { useTranslation } from "@fmc-digital/i18n";
import { MFACodeContainer, MFADestinationText, registerStepCardStyles } from "@fmc-digital/profile";
import { MFASelectorContainer } from "../components/organisms/MFASelectorContainer";
import { LoginContainer } from "@/components/organisms/LoginContainer";
import { config } from "@/config";
import { useMFAVerify } from "../hooks/useMFAVerify";
import { useLoginErrorMessage } from "../hooks/useLoginErrorMessage";
import { useMFANavigation } from "../hooks/useMFANavigation";
import { MaintenanceBanner } from "@/components/molecules/MaintenanceBanner";
import {
  buildOAuthAuthorizeUrl,
  buildOAuthAuthorizeUrlForExistingSession,
  isValidRegisterNowUrl,
  storeOAuthRequestParams,
  isValidReturnUrl,
  getRedirectUrlFromSearch,
  getOAuthRequestParams,
  hasOAuthClientIdContext,
  hasRequiredOAuthAuthorizeParams,
  resolveOAuthRequestParams,
} from "@/utils";
import { trackLoginEvent, LoginEvent, trackRegistrationEvent, RegistrationEvent } from "@/utils/analytics.utils";
import {
  loginFormStyles,
} from "./login-form.styles";
import { useServicingLoginV2Registration } from "@/hooks/useServicingLoginV2Registration";
import { ROUTES } from "@/constants/routes.constants";
import { warmupACPData } from "@/utils/acp-bootstrap.utils";

/**
 * LoginPage
 * Main entry for authentication and MFA flows.
 * Composes atomic UI components and manages all state via custom hooks.
 */
export function LoginPage() {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authRequestParams = getOAuthRequestParams();
    const hasStoredClientId = authRequestParams.includes("client_id");
    const hasClientId = searchParams.has("client_id");
    if (hasClientId) {
    // Case 1: We arrived here as part of a real OAuth authorize request
    // (the dashboard/portal redirected us with client_id, redirect_uri,
    // response_type, code_challenge, etc.). Persist those params so the
    // rest of the login flow (sessionToken -> /v1/authorize) can rebuild
    // the authorize URL after the user submits credentials, then strip
    // them from the address bar so they aren't leaked or re-processed on
    // reload.
      storeOAuthRequestParams(location.search);
      const persistedParams = getOAuthRequestParams();
      if (persistedParams.includes("client_id")) {
        const cleanUrl = `${window.location.pathname}${window.location.hash}`;
        window.history.replaceState(window.history.state, "", cleanUrl);
      }
    } else {
    // Case 2: The Login V2 URL was hit directly, with NO OAuth context
    // (no client_id / PKCE params). We cannot run a valid Okta authorize
    // from here, so we bootstrap the flow by sending the browser to the
    // dashboard. The dashboard owns the Okta OIDC client; its auth guard
    // will immediately call signInWithRedirect(), which sends the user
    // BACK to this Login V2 URL — this time WITH client_id and all the
    // OAuth params. On that second visit we fall into Case 1 above and
    // render the login form with a completable authorize request.
    //
    // Prefer an explicit redirect_url (e.g. a deep link passed through
    // Okta state); otherwise fall back to the configured dashboard URL.

      const redirectUrl = getRedirectUrlFromSearch(location.search);
      const dashboardUri = config.env("dashboardRedirectUri") ?? "";
      const target = redirectUrl ?? dashboardUri;

      if (isValidReturnUrl(target) && !hasStoredClientId) {
         console.log('target found, redirecting to:', target);
         window.location.href = target;
      }else{
        console.error('No target found');
      }
    }
  }, [location.search]);

  // Only show a blocking spinner when we found an existing Okta SSO session and
  // are redirecting. A 404 from sessions/me is expected on first login and must
  // not hide the login form.
  const [ssoRedirectPending, setSsoRedirectPending] = useState(false);

  useEffect(() => {
    if (!hasOAuthClientIdContext(location.search)) {
      return;
    }

    let cancelled = false;

    void getOktaSessionMe().then((session) => {
      if (cancelled || session?.status !== "ACTIVE") {
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has("client_id")) {
        storeOAuthRequestParams(location.search);
      }

      const url = buildOAuthAuthorizeUrlForExistingSession(location.search);
      if (!url) {
        return;
      }

      setSsoRedirectPending(true);
      window.location.assign(url);
    });

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const navigate = useNavigate();
  const { t } = useTranslation();
  // Extract username and password reset flag from navigation state (if present)
  const locationState = location.state as {
    username?: string;
    passwordResetSuccess?: boolean;
    registrationSuccess?: boolean;
  } | null;
  const stateUsername = locationState?.username;
  const { data: servicingLoginV2Registration } = useServicingLoginV2Registration();
  // State for showing success banners (email recovery, password reset)
  const [showEmailRecoveredSuccess, setShowEmailRecoveredSuccess] = useState(false);
  const [showPasswordResetSuccess, setShowPasswordResetSuccess] = useState(false);

  // Show success banners based on navigation state
  useEffect(() => {
    clearError();
    clearMfaError();

    if (
      locationState?.username
      && !locationState?.passwordResetSuccess
      && !locationState?.registrationSuccess
    ) {
      setShowEmailRecoveredSuccess(true);
    } else if (locationState?.passwordResetSuccess) {
      setShowPasswordResetSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login form state and validation logic
  const {
    username,
    setUsername,
    password,
    setPassword,
    clearPassword,
    showPassword,
    setShowPassword,
    emailError,
    passwordError,
  } = useLoginForm(stateUsername ?? "", "");
  // Auth context for MFA and resend logic
  const auth = useAuth();
  const storeStateTokenExpiresAt = useAuthStoreBase(
    (state) => state.stateTokenExpiresAt,
  );
  const storeLoginMfaAnchorExpiresAt = useAuthStoreBase(
    (state) => state.loginMfaAnchorExpiresAt,
  );
  const loginMfaStateTokenExpiresAt =
    auth.mfaState?.stateTokenExpiresAt
    ?? storeStateTokenExpiresAt
    ?? storeLoginMfaAnchorExpiresAt;
  // Disable login if form is invalid
  const isLoginDisabled = !username || !password || !!emailError || !!passwordError;

  // Login logic and error state
  const loginState = useLogin();
  const {
    login,
    isLoading: authLoading,
    error,
    clearError,
  } = loginState;
  const status = (loginState as typeof loginState & { status?: string | null }).status;

  // MFA state and navigation logic
  const {
    currentScreen,
    setCurrentScreen,
    selectedFactor,
    setSelectedFactor,
    mfaError,
    setMfaError,
    mfaErrorCode,
    setMfaErrorCode,
    isSelectingFactor,
    setIsSelectingFactor,
    isVerifying,
    setIsVerifying,
    isResending,
    setIsResending,
    isRedirecting,
    setIsRedirecting,
  } = useMFAFlow();

  const [initialFactorType, setInitialFactorType] = useState<
    "email" | "sms" | undefined
  >(undefined);

  // Utility to clear MFA error and error code
  const clearMfaError = useCallback(() => {
    setMfaError(null);
    setMfaErrorCode(null);
  }, [setMfaError, setMfaErrorCode]);

  const clearAuthMFAState = useCallback(() => {
    const authWithClear = auth as typeof auth & { clearMFAState?: () => void };
    authWithClear.clearMFAState?.();
  }, [auth]);

  // MFA navigation/cancel logic
  const { handleMFACancel, handleNavigateToFactorSelection } = useMFANavigation({
    setCurrentScreen,
    setSelectedFactor,
    setMfaError,
    setMfaErrorCode,
    setInitialFactorType,
    onCancel: clearAuthMFAState,
  });

  /**
   * Handles MFA factor selection and navigation
   * @param factor MFA factor object
   */
  const handleMFANext = useCallback(
    async (factor: {
      readonly id: string;
      readonly factorType: "email" | "sms";
      readonly factorLabel: string;
      readonly profile: { readonly email?: string; readonly phoneNumber?: string };
      readonly link?: string;
    }) => {
      setIsSelectingFactor(true);
      clearMfaError();
      console.debug('[AUTH FLOW] MFA factor selected:', factor);
      
      // Track MFA factor selection
      try {
        if (factor.factorType === "email") {
          trackLoginEvent(LoginEvent.MFA_EMAIL_FACTOR_SELECTED);
        } else if (factor.factorType === "sms") {
          trackLoginEvent(LoginEvent.MFA_PHONE_FACTOR_SELECTED);
        }
      } catch (trackingError) {
        console.error('[AUTH FLOW] Failed to track MFA factor selection', trackingError);
      }
      
      try {
        await auth.selectMFAFactor(factor.id);
        
        // Track OTP triggered after successful factor selection
        try {
          if (factor.factorType === "email") {
            trackLoginEvent(LoginEvent.OTP_EMAIL_TRIGGERED);
          } else if (factor.factorType === "sms") {
            trackLoginEvent(LoginEvent.OTP_PHONE_TRIGGERED);
          }
        } catch (trackingError) {
          console.error('[AUTH FLOW] Failed to track OTP triggered', trackingError);
        }
        
        setSelectedFactor({
          selectedFactorId: factor.id,
          factorType: factor.factorType,
          factorLabel: factor.factorLabel,
          profile: factor.profile,
          link: factor.link,
        });
        setCurrentScreen("mfaCode");
      } catch (err) {
        setMfaError(err instanceof Error ? err.message : "Failed to select MFA factor");
        console.error('[AUTH FLOW] Error selecting MFA factor:', err);
      } finally {
        setIsSelectingFactor(false);
      }
    },
    [auth, setCurrentScreen, setMfaError, setIsSelectingFactor, setSelectedFactor, clearMfaError],
  );


  const handleResend = useCallback(async () => {
    if (!selectedFactor?.selectedFactorId) {
      setMfaError("No factor selected. Please try again.");
      console.error('[AUTH FLOW] Resend attempted with no selectedFactor');
      return;
    }
    clearMfaError();
    setIsResending(true);
    
    // Track OTP resend
    try {
      if (selectedFactor.factorType === "email") {
        trackLoginEvent(LoginEvent.OTP_EMAIL_RESENT);
      } else if (selectedFactor.factorType === "sms") {
        trackLoginEvent(LoginEvent.OTP_PHONE_RESENT);
      }
    } catch (trackingError) {
      console.error('[AUTH FLOW] Failed to track OTP resend', trackingError);
    }
    
    try {
      if (!auth.resendMFACode) throw new Error("resendMFACode method not available");
      console.debug('[AUTH FLOW] Resending MFA code for factorId:', selectedFactor.selectedFactorId);
      await auth.resendMFACode(selectedFactor.selectedFactorId);
    } catch (err) {
      setMfaError((err instanceof Error ? err.message : "") || t("errors.resendFailed", "Failed to resend code. Please try again."));
      console.error('[AUTH FLOW] Error resending MFA code:', err);
    } finally {
      setIsResending(false);
    }
  }, [selectedFactor, auth, t, setMfaError, setIsResending, clearMfaError]);

  const handleStateTokenExpired = useCallback(() => {
    clearAuthMFAState();
    setCurrentScreen("login");
    setSelectedFactor(null);
    clearMfaError();
    clearPassword();
    setShowPassword(false);
  }, [
    clearAuthMFAState,
    setCurrentScreen,
    setSelectedFactor,
    clearMfaError,
    clearPassword,
    setShowPassword,
  ]);

  /**
   * Handles redirect after successful login
   * @param sessionToken Okta session token
   */
  const handleSuccessfulLoginRedirect = useCallback((sessionToken: string | null) => {
    console.debug('[AUTH FLOW] handleSuccessfulLoginRedirect called with sessionToken:', sessionToken);
    if (!sessionToken) {
      console.error('[AUTH FLOW] No sessionToken provided for redirect');
      return;
    }

    const resolvedOAuthParams = resolveOAuthRequestParams(location.search);
    if (!hasRequiredOAuthAuthorizeParams(resolvedOAuthParams)) {
      // No OAuth context (client_id/redirect_uri/response_type) present or persisted —
      // sending sessionToken alone to Okta's /v1/authorize causes it to reject the
      // request and bounce the user to the Okta dashboard / registration page instead
      // of the authenticated portal. Fall back to dashboardRedirectUri directly.
      const dashboardUri = config.env("dashboardRedirectUri") || window.location.origin + "/callback";
      console.debug('[AUTH FLOW] No OAuth context present, falling back to dashboardRedirectUri:', dashboardUri);
      window.location.assign(dashboardUri);
      return;
    }

    const url = buildOAuthAuthorizeUrl(sessionToken, location.search);
    console.info('****[AUTH FLOW] Built OAuth authorize URL:', url);
    window.location.assign(url);

  }, [navigate, location.search]);


   const handleRegisterClick = useCallback(() => {
    const external = config.env("registerNowUrl").trim();
    // Optionally, use a flag from servicingLoginV2Registration to prefer external
    const preferExternal = servicingLoginV2Registration?.useExternalRegistrationFlow === true;
    // For now, always use external if present and valid
    if (preferExternal && external && isValidRegisterNowUrl(external)) {
      window.location.assign(external);
      return;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.CREATE_ACCOUNT_CLICKED);
    } catch {
      // Analytics must not block navigation
    }
    navigate(ROUTES.REGISTER);
  }, [navigate, servicingLoginV2Registration]);
  /**
   * Handles account locked error tracking
   * @param err Error object
   */
  const handleLoginError = useCallback((err: unknown) => {
    let errorCode: string | undefined;
    let errorMessage: string = "";
    if (typeof err === "object" && err !== null) {
      if ("errorCode" in err && typeof (err as any).errorCode === "string") {
        errorCode = (err as any).errorCode;
      }
      if ("message" in err && typeof (err as any).message === "string") {
        errorMessage = (err as any).message;
      }
    } else if (typeof err === "string") {
      errorMessage = err;
    }
    const isAccountLocked = errorCode === 'E0000069' || errorCode === 'E0000047' || errorMessage.toLowerCase().includes('locked') || errorMessage.toLowerCase().includes('too many attempts');
    if (isAccountLocked) {
      try {
        console.info('[AUTH FLOW] Triggering FullStory login event', {
          event: LoginEvent.ACCOUNT_LOCKED,
          source: 'handleLoginError',
        });
        trackLoginEvent(LoginEvent.ACCOUNT_LOCKED);
      } catch (trackingError) {
        console.error('[AUTH FLOW] Failed to trigger FullStory login event', {
          event: LoginEvent.ACCOUNT_LOCKED,
          source: 'handleLoginError',
          trackingError,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (status === "LOCKED_OUT") {
      try {
        console.info('[AUTH FLOW] Triggering FullStory login event', {
          event: LoginEvent.ACCOUNT_LOCKED,
          source: 'statusEffect',
        });
        trackLoginEvent(LoginEvent.ACCOUNT_LOCKED);
      } catch (trackingError) {
        console.error('[AUTH FLOW] Failed to trigger FullStory login event', {
          event: LoginEvent.ACCOUNT_LOCKED,
          source: 'statusEffect',
          trackingError,
        });
      }
    }
  }, [status]);

  // Login form submit handler (from custom hook)
  // Custom login submit handler to handle MFA-required flow
  const [loginAttempted, setLoginAttempted] = useState(false);
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    setShowEmailRecoveredSuccess(false);
    setShowPasswordResetSuccess(false);
    if (emailError || passwordError) return;
    try {
      console.info('[AUTH FLOW] Triggering FullStory login event', {
        event: LoginEvent.CLICKED,
        source: 'handleSubmit',
      });
      trackLoginEvent(LoginEvent.CLICKED);
    } catch (trackingError) {
      console.error('[AUTH FLOW] Failed to trigger FullStory login event', {
        event: LoginEvent.CLICKED,
        source: 'handleSubmit',
        trackingError,
      });
    }
    try {
      clearAuthMFAState();
      console.debug('[AUTH FLOW] Attempting login for username:', username);
      
      // ACP data warm-up: Pre-fetch customer data for Authenticated Customer Portal
      // This runs before authentication to reduce perceived load time for ACP users
      // Only executes when redirect URL indicates this is an ACP (myfreedom domain) request
      warmupACPData(username).catch((error: unknown) => {
        // Silent failure - warm-up is an optimization, not critical to login flow
        console.error('[AUTH FLOW] Error warming up ACP data:', error);
      });
      
      const success = await login({ username, password });
      setLoginAttempted(true);
      if (success) {
        const sessionToken = sessionStorage.getItem('okta_session_token');
        console.debug('[AUTH FLOW] Login success, sessionToken:', sessionToken);
        handleSuccessfulLoginRedirect(sessionToken);
        return;
      }
      // Do not check factors here; let useEffect handle it
    } catch (err) {
      setLoginAttempted(true);
      handleLoginError(err);
      console.error('[AUTH FLOW] Login error:', err);
    }
  };

  // Watch for MFA factors after login attempt and transition to MFA selection
  useEffect(() => {
    if (
      loginAttempted &&
      currentScreen === 'login' &&
      auth.mfaState &&
      Array.isArray(auth.mfaState.factors) &&
      auth.mfaState.factors.length > 0
    ) {
      setInitialFactorType(undefined);
      setCurrentScreen('mfaSelection');
      clearMfaError();
      setLoginAttempted(false);
    }
  }, [
    loginAttempted,
    auth.mfaState,
    auth.mfaState?.factors,
    currentScreen,
    setCurrentScreen,
    clearMfaError,
    setInitialFactorType,
  ]);


  // Error message handler (from custom hook)
  const getErrorMessage = useLoginErrorMessage({ error, t });

  const translateMfaError = useCallback(
    (key: string, fallback?: string) =>
      fallback === undefined
        ? String(t(key as never))
        : String(t(key as never, { defaultValue: fallback })),
    [t],
  );

  // Always call useMFAVerify at the top level to follow Rules of Hooks
  const { handleValidate: handleMFAValidate, handleVerifySuccess: handleMFAVerifySuccess } =
    useMFAVerify({
      auth,
      selectedFactor,
      setIsVerifying,
      setMfaError,
      setMfaErrorCode,
      setIsRedirecting,
      handleSuccessfulLoginRedirect,
      t: translateMfaError,
    });

  // Render MFA selection screen
  if (currentScreen === "mfaSelection") {
    return (
      <div className={cn(loginFormStyles())} data-testid="login-page-mfa-selection">
        <MFASelectorContainer
          factors={auth.mfaState?.factors ?? []}
          onNext={handleMFANext}
          onCancel={handleMFACancel}
          initialFactorType={initialFactorType}
          isLoading={isSelectingFactor}
          error={mfaError}
          onErrorClear={clearMfaError}
        />
      </div>
    );
  }

  // Render MFA code entry screen
  if (currentScreen === "mfaCode" && selectedFactor) {
    return (
      <div className={cn(loginFormStyles())} data-testid="login-page-mfa-code">
        <MFADestinationText
          factorType={selectedFactor.factorType ?? "email"}
          profile={selectedFactor.profile ?? {}}
        />
        <div className={cn(registerStepCardStyles())} data-testid="mfa-otp-card">
          <MFACodeContainer
            factorState={selectedFactor}
            factorsList={auth.mfaState?.factors ?? []}
            onValidate={handleMFAValidate}
            onVerify={handleMFAVerifySuccess}
            onCancel={handleMFACancel}
            onNavigateToFactorSelection={handleNavigateToFactorSelection}
            onResend={handleResend}
            error={mfaError}
            errorCode={mfaErrorCode}
            isLoading={isVerifying}
            isResending={isResending}
            onErrorClear={clearMfaError}
            flow="login"
            stateTokenExpiresAt={loginMfaStateTokenExpiresAt}
            onStateTokenExpired={handleStateTokenExpired}
            onTimerExpired={() => trackLoginEvent(LoginEvent.TIMER_EXPIRED)}
          />
        </div>
      </div>
    );
  }

  // Render loading overlay when redirecting after login/MFA or existing SSO session
  if (isRedirecting || ssoRedirectPending) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        </div>
      </div>
    );
  }

  // Default: Render login form (centered, responsive)
  return (
    <div className={cn("flex w-full flex-col items-start gap-0 self-start")}>
      {/* Maintenance Banner - Full Width, positioned at top */}
      <div className="w-full">
        <MaintenanceBanner />
      </div>

      {/* Spacer for gap between banner and login form */}
      <div className="h-8 md:h-12 lg:h-[64px] w-full flex-shrink-0" />

      {/* Login Form - Centered */}
      <LoginContainer
        username={username}
        password={password}
        showPassword={showPassword}
        setUsername={setUsername}
        setPassword={setPassword}
        setShowPassword={setShowPassword}
        authLoading={authLoading}
        error={error}
        onSubmit={handleSubmit}
        navigate={navigate}
        getErrorMessage={getErrorMessage}
        showEmailRecoveredSuccess={showEmailRecoveredSuccess}
        showPasswordResetSuccess={showPasswordResetSuccess}
        emailError={emailError}
        passwordError={passwordError}
        isLoginDisabled={isLoginDisabled}
        onRegisterClick={handleRegisterClick}
      />

      {/* Responsive spacer to ensure space between login form and footer on all devices */}
      <div className="mb-8 md:mb-16 lg:mb-24 w-full flex-shrink-0" />
    </div>
  );
}
